import React, { useEffect, useState } from 'react';
import { Select, Typography } from 'antd';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import SmallModal from '../../components/Components/Modals/SmallModal';
import { showSuccessNotification, showWarningNotification, showErrorNotification, showInfoNotification } from '../../utils/notifications';
import { LogVariantService } from '../services/Utils/LogVariantService';
import { NotificationService } from '../services/Utils/NotificationService';
import { useParams } from 'react-router-dom';

const { Option } = Select;

const VariantAssignedToModal = ({ isVisible, onCancel, variantId, onSuccess, currentAssignee, assignmentDate }) => {
    const db = getFirestore();
    const [modellistaProfiles, setModellistaProfiles] = useState([]);
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [userProfiles, setUserProfiles] = useState({});
    const { brandId, productId } = useParams();

    // Fetch user profiles for display names
    useEffect(() => {
        const fetchUserProfiles = async () => {
            try {
                const profilesRef = collection(db, "Profiles");
                const profilesSnapshot = await getDocs(profilesRef);
                const profilesData = {};

                profilesSnapshot.forEach((doc) => {
                    const data = doc.data();
                    const email = data._email || data.email;
                    if (email) {
                        profilesData[email] = {
                            firstName: data.firstName || '',
                            lastName: data.lastName || ''
                        };
                    }
                });

                setUserProfiles(profilesData);
            } catch (error) {
                console.error("Error fetching user profiles:", error);
            }
        };

        if (isVisible) {
            fetchUserProfiles();
        }
    }, [isVisible, db]);

    // Function to get user's full name from email
    const getUserFullName = (email) => {
        if (!email) return '';

        // Check if email exists exactly as is
        const userProfile = userProfiles[email];
        if (userProfile && userProfile.firstName && userProfile.lastName) {
            return `${userProfile.firstName} ${userProfile.lastName}`;
        }

        // Try case-insensitive search
        const lowerEmail = email.toLowerCase().trim();
        const matchingKey = Object.keys(userProfiles).find(key => key.toLowerCase().trim() === lowerEmail);
        if (matchingKey) {
            const profile = userProfiles[matchingKey];
            if (profile && profile.firstName && profile.lastName) {
                return `${profile.firstName} ${profile.lastName}`;
            }
        }

        return email; // Fallback to email if profile not found
    };

    useEffect(() => {
        if (isVisible) {
            const fetchModellisti = async () => {
                try {
                    const q = query(collection(db, "Profiles"), where("role", "in", ["Modellista", "ModellerSupervisor"]));
                    const querySnapshot = await getDocs(q);
                    const profiles = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setModellistaProfiles(profiles);
                } catch (error) {
                    console.error("Errore nel recupero dei profili Modellista:", error);
                    showErrorNotification("Errore", "Impossible to load the list of modellers");
                }
            };
            fetchModellisti();
        } else {
            // Reset dello stato quando la modale si chiude
            setSelectedAssignee(null);
        }
    }, [isVisible, db]);

    const handleAssign = async () => {
        if (!selectedAssignee) {
            showWarningNotification("Attention", "Select a modeller.");
            return;
        }

        if (!variantId) {
            showErrorNotification("Error", "Variant ID is not valid");
            return;
        }

        setIsAssigning(true);

        try {
            const timestamp = new Date();
            const formattedDate = timestamp.toLocaleDateString('it-IT');
            const formattedTime = timestamp.toLocaleTimeString('it-IT');
            const assignedDateString = `${formattedDate}, ${formattedTime}`;

            // Ottieni i dati della variante per estrarre skuModel
            const variantRef = doc(db, "Variants", variantId);
            const variantSnap = await getDoc(variantRef);
            let skuModel = "";
            let modelName = "";

            if (variantSnap.exists()) {
                const variantData = variantSnap.data();
                skuModel = variantData.skuModel || "";
                modelName = variantData.modelName || variantData.glassesName || "";
            }

            // Verifica se l'utente ha accesso al brand
            let brandAccessGranted = false;

            // Trova il profilo dell'utente selezionato
            const userQuery = query(collection(db, "Profiles"), where("_email", "==", selectedAssignee));
            const userQuerySnapshot = await getDocs(userQuery);

            if (!userQuerySnapshot.empty) {
                const userDoc = userQuerySnapshot.docs[0];
                const userData = userDoc.data();

                // Controlla se l'utente ha già il brand nella sua lista
                const userBrands = userData.list_main_brands || [];

                if (userBrands.includes(brandId)) {
                    brandAccessGranted = true;
                } else {
                    // Aggiungi il brand alla lista dell'utente
                    const updatedBrands = [...userBrands, brandId];
                    await updateDoc(doc(db, "Profiles", userDoc.id), {
                        list_main_brands: updatedBrands
                    });
                    brandAccessGranted = true;

                    showInfoNotification(
                        "Brand Access Granted",
                        `${userData.firstName || selectedAssignee} now has access to this brand.`
                    );
                }
            }

            // Aggiorna il documento della variante
            await updateDoc(variantRef, {
                assignedTo: selectedAssignee,
                assignmentDate: assignedDateString
            });

            // Registra l'assegnazione nel log
            await LogVariantService.logAssignVariant(variantId, selectedAssignee);

            // Crea una notifica per l'assegnazione
            await NotificationService.createVariantAssignmentNotification(
                variantId,
                brandId,
                skuModel,
                selectedAssignee,
                false,
                modelName,
                productId
            );

            showSuccessNotification("Save successful", "Variant assigned successfully!");
            onSuccess();
        } catch (error) {
            console.error(`Errore durante l'assegnazione della variante ${variantId}:`, error);

            let errorMessage = "Errore durante l'assegnazione";
            if (error.message) {
                errorMessage += `: ${error.message}`;
            }

            showErrorNotification("Errore", errorMessage);
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <SmallModal
            title="Assign the single variant to the modeler:"
            visible={isVisible}
            onClose={onCancel}
            onConfirm={handleAssign}
            cancelText="Cancel"
            confirmText={isAssigning ? "Assegnazione in corso..." : "Assign"}
            confirmButtonProps={{ disabled: isAssigning, loading: isAssigning }}
        >
            {currentAssignee && (
                <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '8px' }}>
                    <Typography.Title level={5} style={{ marginBottom: '8px' }}>Current Assignment</Typography.Title>
                    <p><strong>Assignee:</strong> {getUserFullName(currentAssignee)}</p>
                    <p><strong>Date:</strong> {assignmentDate}</p>
                </div>
            )}
            <div style={{ marginBottom: '16px' }}>
                <Typography.Title level={5} style={{ marginBottom: '8px' }}>Select New Assignee</Typography.Title>
                <Select
                    placeholder="Select a modeler"
                    style={{ width: '100%' }}
                    value={selectedAssignee}
                    onChange={setSelectedAssignee}
                    disabled={isAssigning}
                >
                    {modellistaProfiles.map(profile => (
                        <Option key={profile.id} value={profile._email || profile.email}>
                            {profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : profile.email}
                        </Option>
                    ))}
                </Select>
            </div>
            <div style={{ marginTop: '16px', color: '#888' }}>
                Date: {new Date().toLocaleString('it-IT')}
            </div>
            {isAssigning && (
                <div style={{ marginTop: '16px', color: '#1890ff' }}>
                    Assegnazione in corso, si prega di attendere...
                </div>
            )}
        </SmallModal>
    );
};

export default VariantAssignedToModal;